import React, { useState, useEffect, useMemo } from "react";
import type {
  IAssessmentDetailsStepProps,
  IAssessmentFormData,
} from "./definitions";
import styles from "./style.module.scss";
import {
  Box,
  Checkbox,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  Alert,
} from "@amzn/awsui-components-react/polaris";
import {
  ControlledSelect,
  ControlledTextarea,
  ControlledDatePicker,
} from "@amzn/react-hook-form-polaris";
import { get, useFormState } from "react-hook-form";
import MonetaryInput from "components/monetary-input/MonetaryInput";
import { useGetScanResultQuery } from "pages/draft-overview/endpoints";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import { useListTaxAreaByTeamQuery } from "pages/team_settings/endpoints";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import { GaiaConstants } from "utils";
import {
  useBreakdownState,
  calculateAssessmentTotal,
  calculateBreakdownTotals,
  makeUpdateAmount,
  parseBreakdownsFromScanResult,
  parseTaxYearAreaBreakdownsFromScanResult,
} from "./utils";
import { useLocalStorage } from "hooks/useLocalStorage";
import WizardFilesSection from "components/wizard-files-section";
import OutcomeBreakdownRow from "components/outcome-breakdown-row";
import { TaxYearBreakdown, TaxYearAreaBreakdown } from "models/schemas";
import { formatDateValue } from "utils/date-format";

const AssessmentDetailsStep: React.FC<IAssessmentDetailsStepProps> = ({
  shortIdForScanJob,
  currencySign,
  isScanWizard = false,
  externalFormData,
  onFormDataChange,
  taxAreaOptions = [],
  selectedAssessmentId,
  gaiaObjectId,
  isCreateMode,
  category,
  stagedFiles = [],
  onStagedFilesChange,
  control,
  setValue,
  onBreakdownChange,
}) => {
  const formState = useFormState({ control });
  const {
    data: scanResultData,
    isLoading,
    error,
  } = useGetScanResultQuery(
    { shortId: shortIdForScanJob! },
    { skip: !isScanWizard || !shortIdForScanJob }
  );
  const uiContent: any = useGaiaObjectUiContent(category);
  const storageKey = `assessment-step-${shortIdForScanJob}`;

  const [internalFormData, setInternalFormData] =
    useLocalStorage<IAssessmentFormData>(storageKey, {
      dateReceived: "",
      type: isScanWizard ? "Preliminary" : "",
      notes: "",
      amounts: {
        totalTaxAmount: "0",
        totalPenaltyAmount: "0",
        totalInterestAmount: "0",
        totalOtherAmount: "0",
      },
      breakdowns: {
        byTaxYear: false,
        byTaxArea: false,
        byTaxYearArea: false,
      },
    });

  const formData =
    !isScanWizard && externalFormData ? externalFormData : internalFormData;
  const setFormData =
    !isScanWizard && onFormDataChange ? onFormDataChange : setInternalFormData;

  const [parsedTaxYearBreakdowns, setParsedTaxYearBreakdowns] = useState<
    TaxYearBreakdown[]
  >([]);
  const [parsedTaxYearAreaBreakdowns, setParsedTaxYearAreaBreakdowns] =
    useState<TaxYearAreaBreakdown[]>([]);
  const { teamName } = useTeamNameSelector();
  const { data: listTaxAreaData } = useListTaxAreaByTeamQuery(
    { teamName },
    {
      refetchOnMountOrArgChange: true,
      skip: !teamName || teamName === GaiaConstants.PLACE_HOLDER,
    }
  );
  const teamTaxAreaOptions = useMemo(
    () =>
      listTaxAreaData?.taxAreas?.taxAreas
        ?.filter((ta) => ta.status === "Active")
        ?.map((ta) => ({ value: ta.taxAreaId, label: ta.title })) || [],
    [listTaxAreaData]
  );

  const {
    state: breakdownState,
    handlers: breakdownHandlers,
    disabledStates,
  } = useBreakdownState(taxAreaOptions.length > 0);

  useEffect(() => {
    if (formData.breakdowns) {
      const hasChanged =
        breakdownState.byTaxYear !== formData.breakdowns.byTaxYear ||
        breakdownState.byTaxArea !== formData.breakdowns.byTaxArea ||
        breakdownState.byTaxYearArea !== formData.breakdowns.byTaxYearArea;

      if (hasChanged) {
        if (formData.breakdowns.byTaxYear)
          breakdownHandlers.handleTaxYearChange(true);
        if (formData.breakdowns.byTaxArea)
          breakdownHandlers.handleTaxAreaChange(true);
        if (formData.breakdowns.byTaxYearArea)
          breakdownHandlers.handleTaxYearAreaChange(true);
      }
    }
  }, [
    formData.breakdowns?.byTaxYear,
    formData.breakdowns?.byTaxArea,
    formData.breakdowns?.byTaxYearArea,
  ]);

  useEffect(() => {
    // Only update if breakdowns actually changed to prevent infinite loop
    if (
      formData.breakdowns?.byTaxYear !== breakdownState.byTaxYear ||
      formData.breakdowns?.byTaxArea !== breakdownState.byTaxArea ||
      formData.breakdowns?.byTaxYearArea !== breakdownState.byTaxYearArea
    ) {
      setFormData({
        ...formData,
        breakdowns: breakdownState,
      });
      onBreakdownChange?.(breakdownState);
    }
  }, [breakdownState]);

  const handleFilesChange = onStagedFilesChange || (() => {});

  const updateAmount = makeUpdateAmount(formData, setFormData, control);
  const assessmentTypeOptions = uiContent?.assessmentTypeOptions || [];

  useEffect(() => {
    if (scanResultData?.scanResult && uiContent) {
      const result = scanResultData.scanResult as any;

      const taxYearAreaBreakdowns =
        parseTaxYearAreaBreakdownsFromScanResult(result);
      const matchedTaxArea = teamTaxAreaOptions.find(
        (ta) => ta.label.toLowerCase() === (result.taxArea || "").toLowerCase()
      );
      const usesTaxYearArea =
        taxYearAreaBreakdowns.length > 0 && !!matchedTaxArea;
      const breakdowns = usesTaxYearArea
        ? []
        : parseBreakdownsFromScanResult(result).filter(
            (b) => b.category !== "No Information"
          );
      const resolvedBreakdowns = usesTaxYearArea
        ? taxYearAreaBreakdowns.map((b) => ({
            ...b,
            taxAreaId: matchedTaxArea!.value,
          }))
        : [];

      setParsedTaxYearBreakdowns(breakdowns);
      setParsedTaxYearAreaBreakdowns(resolvedBreakdowns);
      const dateReceived = result.dateReceived
        ? formatDateValue(parseInt(result.dateReceived))
        : "";

      setFormData({
        ...formData,
        dateReceived,
        type: isScanWizard ? "Preliminary" : formData.type,
        notes: result.notes || formData.notes,
        parsedTaxYearBreakdowns: breakdowns,
        parsedTaxYearAreaBreakdowns: resolvedBreakdowns,
        amounts: {
          totalTaxAmount: result.total_tax_amount || "0",
          totalPenaltyAmount: result.total_penalty_amount || "0",
          totalInterestAmount: result.total_interest_amount || "0",
          totalOtherAmount: result.total_other_amount || "0",
        },
      });
    }
  }, [scanResultData, uiContent, teamTaxAreaOptions]);

  // Sync formData with react-hook-form to display values in UI
  useEffect(() => {
    if (!setValue) return;
    if (formData.dateReceived)
      setValue("dateReceived" as any, formData.dateReceived);
    if (formData.type) setValue("type" as any, formData.type);
    if (formData.notes) setValue("notes" as any, formData.notes);
  }, [formData.dateReceived, formData.type, formData.notes, setValue]);

  const total = useMemo(() => {
    return calculateAssessmentTotal({
      tax: formData.amounts.totalTaxAmount,
      penalty: formData.amounts.totalPenaltyAmount,
      interest: formData.amounts.totalInterestAmount,
      other: formData.amounts.totalOtherAmount,
    });
  }, [formData.amounts]);

  if (isLoading) {
    return (
      <Container>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <div className={styles.loadingText}>Loading assessment data...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert type="error" header="Error loading assessment data">
          Unable to load scan result data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container>
        <SpaceBetween direction="vertical" size="m">
          <div className={styles.firstRowWrapper}>
            <div className={styles.dateCustomPicker}>
              <FormField
                label="Date received"
                errorText={get(formState.errors, "dateReceived.message")}
              >
                <ControlledDatePicker
                  name="dateReceived"
                  control={control}
                  placeholder="YYYY/MM/DD"
                  onChange={({ detail }) => {
                    setFormData({
                      ...formData,
                      dateReceived: detail.value,
                    });
                  }}
                />
              </FormField>
            </div>
            <div className={styles.typeBox}>
              <FormField
                label="Type"
                errorText={get(formState.errors, "type.message")}
              >
                <ControlledSelect
                  name="type"
                  options={assessmentTypeOptions}
                  control={control}
                  placeholder="Select assessment type"
                  onChange={({ detail }) => {
                    setFormData({
                      ...formData,
                      type: detail.selectedOption.value || "",
                    });
                  }}
                />
              </FormField>
            </div>
          </div>
          <div className={styles.notesBox}>
            <FormField
              label="Notes"
              errorText={get(formState.errors, "notes.message")}
            >
              <ControlledTextarea
                name="notes"
                control={control}
                placeholder=""
                rows={3}
                onChange={({ detail }) => {
                  setFormData({
                    ...formData,
                    notes: detail.value,
                  });
                }}
              />
            </FormField>
          </div>
        </SpaceBetween>
      </Container>
      {!isScanWizard && (
        <WizardFilesSection
          parentId={selectedAssessmentId}
          gaiaObjectId={gaiaObjectId || ""}
          category="assessments"
          stagedFiles={stagedFiles}
          onStagedFilesChange={handleFilesChange}
          isCreateMode={isCreateMode || false}
        />
      )}
      <Container
        header={
          <div className={styles.headerContainer}>
            <Header variant="h2">Assessment overview</Header>
          </div>
        }
      >
        <div className={styles.overviewWrapper}>
          <FormField
            label="Tax"
            controlId="totalTaxAmount"
            errorText={get(formState.errors, "totalTaxAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalTaxAmount}
              onChange={(value) => updateAmount("totalTaxAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Penalty"
            controlId="totalPenaltyAmount"
            errorText={get(formState.errors, "totalPenaltyAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalPenaltyAmount}
              onChange={(value) => updateAmount("totalPenaltyAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Interest"
            controlId="totalInterestAmount"
            errorText={get(formState.errors, "totalInterestAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalInterestAmount}
              onChange={(value) => updateAmount("totalInterestAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Other"
            controlId="totalOtherAmount"
            errorText={get(formState.errors, "totalOtherAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalOtherAmount}
              onChange={(value) => updateAmount("totalOtherAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <div className={styles.dollarInput}>
            <FormField label="Total">
              <div className={styles.inputContainer}>
                <span className={styles.dollarSign}> {currencySign} </span>
                <input
                  value={total}
                  readOnly={true}
                  className={styles.inputField}
                />
              </div>
            </FormField>
          </div>
        </div>

        {!isScanWizard && (
          <>
            <br />
            <Box>
              <SpaceBetween direction="vertical" size="s">
                <div>
                  <Header variant="h3">Further breakdown the amount by</Header>
                </div>
                <div>
                  <SpaceBetween direction="vertical" size="xs">
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearChange(detail.checked)
                      }
                      checked={breakdownState.byTaxYear}
                      disabled={disabledStates.taxYear}
                    >
                      Tax year
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxAreaChange(detail.checked)
                      }
                      checked={breakdownState.byTaxArea}
                      disabled={disabledStates.taxArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax area
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearAreaChange(
                          detail.checked
                        )
                      }
                      checked={breakdownState.byTaxYearArea}
                      disabled={disabledStates.taxYearArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax year & area
                    </Checkbox>
                  </SpaceBetween>
                </div>
              </SpaceBetween>
            </Box>
          </>
        )}
      </Container>
      {parsedTaxYearBreakdowns.length > 0 && (
        <Container
          header={<Header variant="h2">Assessment by tax year</Header>}
        >
          <SpaceBetween direction="vertical" size="xl">
            {parsedTaxYearBreakdowns.map((breakdown, index) => (
              <OutcomeBreakdownRow
                key={breakdown.outcomeBreakdownId || index}
                breakdown={breakdown}
                currencySign={currencySign}
                isFirstRow={index === 0}
                setBreakdownInList={(updatedBreakdown) => {
                  const updated = parsedTaxYearBreakdowns.map((b) =>
                    b.outcomeBreakdownId === updatedBreakdown.outcomeBreakdownId
                      ? (updatedBreakdown as TaxYearBreakdown)
                      : b
                  );
                  setParsedTaxYearBreakdowns(updated);
                  const { totalTax, totalPenalty, totalInterest, totalOther } =
                    calculateBreakdownTotals(updated);
                  setFormData({
                    ...formData,
                    parsedTaxYearBreakdowns: updated,
                    amounts: {
                      totalTaxAmount: String(totalTax),
                      totalPenaltyAmount: String(totalPenalty),
                      totalInterestAmount: String(totalInterest),
                      totalOtherAmount: String(totalOther),
                    },
                  });
                }}
                onRemove={() => {}}
                showRemoveButton={false}
              />
            ))}
          </SpaceBetween>
        </Container>
      )}
      {parsedTaxYearAreaBreakdowns.length > 0 && (
        <Container
          header={<Header variant="h2">Assessment by tax year & area</Header>}
        >
          <SpaceBetween direction="vertical" size="xl">
            {parsedTaxYearAreaBreakdowns.map((breakdown, index) => (
              <OutcomeBreakdownRow
                key={`${breakdown.outcomeBreakdownId || index}-${
                  breakdown.taxAreaId
                }`}
                breakdown={breakdown}
                currencySign={currencySign}
                isFirstRow={index === 0}
                taxAreaOptions={teamTaxAreaOptions}
                setBreakdownInList={(updatedBreakdown) => {
                  const updated = parsedTaxYearAreaBreakdowns.map((b) =>
                    b.outcomeBreakdownId === updatedBreakdown.outcomeBreakdownId
                      ? (updatedBreakdown as TaxYearAreaBreakdown)
                      : b
                  );
                  setParsedTaxYearAreaBreakdowns(updated);
                  const { totalTax, totalPenalty, totalInterest, totalOther } =
                    calculateBreakdownTotals(updated);
                  setFormData({
                    ...formData,
                    amounts: {
                      totalTaxAmount: String(totalTax),
                      totalPenaltyAmount: String(totalPenalty),
                      totalInterestAmount: String(totalInterest),
                      totalOtherAmount: String(totalOther),
                    },
                  });
                }}
                onRemove={() => {}}
                showRemoveButton={false}
              />
            ))}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default AssessmentDetailsStep;
